import React, { useEffect, useCallback } from 'react';
import { Drawer, Form, Input, InputNumber, Button, Space, message, Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  CREATE_DRIVER_LEARNING_CENTER_FAQ_CATEGORY,
  UPDATE_DRIVER_LEARNING_CENTER_FAQ_CATEGORY,
} from '../graphql/mutations';
import { DriverLearningCenterFaqNode } from '../graphql/types';
import { slugify, createWithSlugRetry, generateFallbackSlug } from '../utils/slugify';
import { resolveLearningCenterErrorMessage } from '../utils/errorMessage';

interface FaqCategoryDrawerProps {
  visible: boolean;
  category: DriverLearningCenterFaqNode | null;
  onClose: () => void;
  onSuccess: () => void;
}

const FaqCategoryDrawer: React.FC<FaqCategoryDrawerProps> = ({ visible, category, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isEdit = !!category;

  const [createCategory, { loading: creating }] = useMutation(CREATE_DRIVER_LEARNING_CENTER_FAQ_CATEGORY);
  const [updateCategory, { loading: updating }] = useMutation(UPDATE_DRIVER_LEARNING_CENTER_FAQ_CATEGORY);
  // 用 Modal.useModal 而非静态 Modal.confirm：静态方法在 React 18 createRoot 下
  // 无法可靠同步内部状态，确定按钮 loading 可能卡死。
  const [modal, contextHolder] = Modal.useModal();

  useEffect(() => {
    if (!visible) return;
    if (category) {
      form.setFieldsValue({
        slug: category.slug,
        titleZh: category.titleZh,
        titleEn: category.titleEn,
        titleEs: category.titleEs,
        sortOrder: category.sortOrder,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ sortOrder: 0 });
    }
  }, [visible, category, form]);

  const doSubmit = useCallback(async (values: Record<string, any>) => {
    if (isEdit && category) {
      await updateCategory({
        variables: {
          id: category.id,
          input: {
            titleZh: values.titleZh,
            titleEn: values.titleEn,
            titleEs: values.titleEs,
            sortOrder: values.sortOrder,
          },
        },
      });
    } else {
      const baseSlug = slugify(values.titleEn) || generateFallbackSlug();
      await createWithSlugRetry(baseSlug, (slug) =>
        createCategory({
          variables: {
            input: {
              slug,
              titleZh: values.titleZh,
              titleEn: values.titleEn,
              titleEs: values.titleEs,
              sortOrder: values.sortOrder,
            },
          },
        }),
      );
    }
    message.success(t('learningCenterConfig.messages.saveSuccess'));
    onSuccess();
  }, [isEdit, category, createCategory, updateCategory, onSuccess, t]);

  const handleSubmit = useCallback(async () => {
    let values: Record<string, any>;
    try {
      values = await form.validateFields();
    } catch (err: any) {
      return; // 表单校验失败，Antd 已在界面标红提示
    }

    const submitSafely = async () => {
      try {
        await doSubmit(values);
      } catch (err: any) {
        const errorMessage = resolveLearningCenterErrorMessage(err, t);
        if (errorMessage) {
          message.error(errorMessage);
        }
        // 不 rethrow：Modal.onOk 收到 rejected Promise 时 CRA 会弹出运行时红屏
      }
    };

    if (!isEdit) {
      modal.confirm({
        title: t('learningCenterConfig.messages.createCategoryConfirmTitle'),
        content: t('learningCenterConfig.messages.createCategoryConfirmContent'),
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
        onOk: submitSafely,
      });
      return;
    }

    await submitSafely();
  }, [form, isEdit, doSubmit, modal, t]);

  return (
    <>
    {contextHolder}
    <Drawer
      title={
        <span className="text-lg font-semibold border-l-4 border-purple-600 pl-2">
          {isEdit ? t('learningCenterConfig.category.editTitle') : t('learningCenterConfig.category.createTitle')}
        </span>
      }
      width={480}
      visible={visible}
      onClose={onClose}
      closable={false}
      extra={<Button type="text" onClick={onClose} icon={<CloseOutlined />} />}
      footer={
        <Space className="flex justify-end">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="primary" onClick={handleSubmit} loading={creating || updating}>
            {t('common.confirm')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        {isEdit && (
          <Form.Item name="slug" label={t('learningCenterConfig.category.slug')}>
            <Input disabled />
          </Form.Item>
        )}
        <Form.Item
          name="titleZh"
          label={t('learningCenterConfig.category.titleZh')}
          rules={[{ required: true, message: String(t('common.required')) }]}
        >
          <Input maxLength={128} />
        </Form.Item>
        <Form.Item
          name="titleEn"
          label={t('learningCenterConfig.category.titleEn')}
          rules={[{ required: true, message: String(t('common.required')) }]}
        >
          <Input maxLength={128} />
        </Form.Item>
        <Form.Item
          name="titleEs"
          label={t('learningCenterConfig.category.titleEs')}
          rules={[{ required: true, message: String(t('common.required')) }]}
        >
          <Input maxLength={128} />
        </Form.Item>
        <Form.Item name="sortOrder" label={t('learningCenterConfig.category.sortOrder')}>
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Drawer>
    </>
  );
};

export default FaqCategoryDrawer;
